import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Printer, Trash2, Plus, Wrench, StickyNote, Package2, Pencil, Camera, X, Upload, History, AlertTriangle } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { inventoryApi, storesApi, usersApi } from '../lib/api';
import { useAssignees, groupAssignees } from '../lib/useAssignees';
import { usePermissions } from '../lib/usePermissions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const TYPE_LABEL = { note: 'Nota', maintenance: 'Manutenzione', replacement: 'Ricambio' };
const TYPE_ICON = { note: StickyNote, maintenance: Wrench, replacement: Package2 };

function maintenanceStatus(events) {
  const lastMant = events.find(e => e.type === 'maintenance' || e.type === 'replacement');
  if (!lastMant) return { label: 'Nessuna mant.', tone: 'neutral' };
  const daysAgo = (Date.now() - new Date(lastMant.date).getTime()) / (1000 * 60 * 60 * 24);
  if (daysAgo <= 90) return { label: 'Mant. recente', tone: 'fresh' };
  return { label: 'Mant. vecchia', tone: 'warn' };
}

function MaintenanceAlertBanner({ alert }) {
  if (!alert || alert.status === 'ok' || alert.status === 'none') return null;
  const isOverdue = alert.status === 'overdue';
  const refLabel = alert.reference === 'maintenance' ? 'ultima manutenzione'
                 : alert.reference === 'purchase' ? 'data di acquisto'
                 : null;
  const months = alert.months_since_reference;
  return (
    <div
      className={`px-4 py-3 border flex items-start gap-3 ${
        isOverdue
          ? 'bg-red-50 border-red-300 text-red-800'
          : 'bg-amber-50 border-amber-300 text-amber-800'
      }`}
      data-testid={`maintenance-alert-banner-${alert.status}`}
    >
      <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="flex-1 text-sm">
        <p className="font-semibold">
          {isOverdue ? 'Manutenzione in ritardo' : 'Manutenzione consigliata'}
        </p>
        <p className="text-xs mt-0.5 opacity-90">
          Soglia categoria: ogni {alert.recommended_months} mesi.
          {refLabel && months != null && (
            <> Sono passati <b>{months}</b> mesi dalla {refLabel}.</>
          )}
          {!refLabel && (
            <> Nessuna data di acquisto né manutenzione registrata.</>
          )}
        </p>
      </div>
    </div>
  );
}

export default function InventoryItemPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Quando si arriva da `/stores/{id}?tab=inventory` il back deve tornare lì,
  // non al magazzino globale. Lo state viene passato dal Link nello
  // StoreDashboardPage::InventoryTab.
  const backTo = location.state?.backTo || '/inventory';
  const backLabel = location.state?.backLabel || 'Inventario';
  const [data, setData] = useState(null);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [eventForm, setEventForm] = useState({
    type: 'note', date: new Date().toISOString().slice(0, 10),
    description: '', cost: ''
  });
  const [savingEvt, setSavingEvt] = useState(false);
  const [eventLightbox, setEventLightbox] = useState(null); // {eventId, attId, filename}
  const [attachments, setAttachments] = useState([]);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [itemLightbox, setItemLightbox] = useState(null); // {attId, filename}
  const [assignments, setAssignments] = useState([]);

  const isOps = user?.role === 'operations_manager';
  const isAM = user?.role === 'area_manager';
  const { has } = usePermissions();
  // Refactor 2026-05-29: gating granulare via permessi RBAC dinamici.
  // Hook chiamato sempre (no early return prima) per rispettare rules-of-hooks.
  const canEdit = has('inventory.item.edit') || has('inventory.assign');
  const canDelete = has('inventory.item.delete');
  const canAddMaintenance = isOps || isAM;
  const canAddNote = isOps || isAM || user?.role === 'store_user';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, st, us, atts, asg] = await Promise.all([
        inventoryApi.getItem(itemId),
        storesApi.list({ include_special: true }),
        usersApi.list().catch(() => ({ data: [] })),
        inventoryApi.listAttachments(itemId).catch(() => ({ data: [] })),
        inventoryApi.listAssignments(itemId).catch(() => ({ data: [] })),
      ]);
      setData(res.data);
      setStores(Array.isArray(st.data) ? st.data : (st.data?.stores || []));
      setUsers(us.data || []);
      setAttachments(atts.data || []);
      setAssignments(asg.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento oggetto');
      navigate(backTo);
    } finally {
      setLoading(false);
    }
  }, [itemId, backTo, navigate]);

  useEffect(() => { load(); }, [load]);

  async function uploadItemFiles(files) {
    const list = Array.from(files || []).filter(f => f && f.type?.startsWith('image/'));
    if (!list.length) return;
    const remaining = 10 - attachments.length;
    if (list.length > remaining) {
      toast.error(`Massimo 10 foto per oggetto (${remaining} rimanenti)`);
      return;
    }
    setUploadingAtt(true);
    try {
      for (const f of list) {
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${f.name}: troppo grande (max 5MB)`);
          continue;
        }
        await inventoryApi.uploadAttachment(itemId, f);
      }
      toast.success('Foto caricate');
      const r = await inventoryApi.listAttachments(itemId);
      setAttachments(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore upload');
    } finally {
      setUploadingAtt(false);
    }
  }

  async function removeItemAttachment(attId) {
    if (!window.confirm('Rimuovere questa foto?')) return;
    try {
      await inventoryApi.deleteAttachment(itemId, attId);
      setAttachments(prev => prev.filter(a => a.attachment_id !== attId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  }

  async function addEvent() {
    if (!eventForm.description.trim()) {
      toast.error('Inserisci una descrizione');
      return;
    }
    setSavingEvt(true);
    try {
      const payload = { type: eventForm.type, date: eventForm.date, description: eventForm.description };
      if (eventForm.cost) payload.cost = Number(eventForm.cost);
      await inventoryApi.addEvent(itemId, payload);
      toast.success('Evento aggiunto');
      setEventForm({ type: 'note', date: new Date().toISOString().slice(0, 10), description: '', cost: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally {
      setSavingEvt(false);
    }
  }

  async function removeEvent(eventId) {
    if (!window.confirm('Rimuovere questo evento?')) return;
    try {
      await inventoryApi.deleteEvent(itemId, eventId);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  }

  async function deleteItem() {
    if (!window.confirm(`Eliminare definitivamente ${data.item.code}? Storico eventi sarà cancellato.`)) return;
    try {
      await inventoryApi.deleteItem(itemId);
      toast.success('Oggetto eliminato');
      navigate(backTo);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  }

  async function printLabel() {
    try {
      const res = await inventoryApi.labelsPdf([itemId]);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `etichetta-${data.item.code}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore stampa etichetta');
    }
  }

  if (loading || !data) return <div className="p-8 text-slate-500">Caricamento…</div>;

  const item = data.item;
  const events = data.events || [];
  const storeName = stores.find(s => s.store_id === item.store_id)?.display_name
                  || stores.find(s => s.store_id === item.store_id)?.name || '—';
  const status = maintenanceStatus(events);
  const pillClass =
    status.tone === 'fresh' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : status.tone === 'warn' ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-slate-50 text-slate-600 border-slate-200';

  const maintenanceEvents = events.filter(e => e.type !== 'note');
  const noteEvents = events.filter(e => e.type === 'note');

  return (
    <div className="p-4 md:p-8 max-w-[1920px] space-y-8" data-testid="inventory-item-page">
      {/* ===== HEADER ===== */}
      <header className="flex items-start justify-between gap-4 flex-wrap pb-6 border-b border-slate-200">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md border border-slate-300 bg-slate-50 flex items-center justify-center text-xs font-bold tracking-wider text-slate-700 shrink-0">
            {(item.brand_code || 'LIB').slice(0, 3).toUpperCase()}
          </div>
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Registro Strumentazione</h2>
            <p className="text-sm text-slate-500 mt-1">Monitoraggio attrezzature — rete saloni</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={printLabel} data-testid="print-single-btn">
            <Printer className="w-4 h-4 mr-2" />Etichetta
          </Button>
        </div>
      </header>

      {/* ===== BACK + CODE + STATUS PILL ===== */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link
            to={backTo}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-50"
            data-testid="back-to-inventory"
          >
            <ArrowLeft className="w-4 h-4" />{backLabel}
          </Link>
          <span className="px-3 py-1.5 rounded-md bg-slate-100 border border-slate-200 text-sm font-mono font-semibold text-slate-800 tracking-wider">
            {item.code}
          </span>
        </div>
        <span
          className={`inline-block px-3 py-1 rounded-full border text-xs font-medium ${pillClass}`}
          data-testid="item-status-pill"
        >
          {status.label}
        </span>
      </div>

      <MaintenanceAlertBanner alert={item.maintenance_alert} />

      {/* ===== GRIGLIA 2x2 ===== */}
      <div className="grid md:grid-cols-2 gap-5">
        {/* INFORMAZIONI PRODOTTO */}
        <DetailCard title="Informazioni prodotto" testId="card-info">
          <DetailField label="Categoria" value={item.category_name} />
          <DetailField label="Modello / marca" value={item.model || '—'} />
          <DetailField label="Note" value={item.description || '—'} multiline />
        </DetailCard>

        {/* ACQUISTO */}
        <DetailCard title="Acquisto" testId="card-purchase">
          <DetailField
            label="Data acquisto"
            value={item.purchase_date ? new Date(item.purchase_date).toLocaleDateString('it-IT') : '—'}
          />
          <DetailField
            label="Prezzo"
            value={item.purchase_price != null ? `€${Number(item.purchase_price).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : '—'}
          />
          <DetailField label="Brand" value={item.brand_code || '—'} />
        </DetailCard>

        {/* UBICAZIONE */}
        <DetailCard title="Ubicazione" testId="card-location">
          <div>
            <p className="text-xs text-slate-500">Negozio</p>
            {item.store_id ? (
              <Link
                to={`/stores/${item.store_id}?tab=inventory`}
                className="mt-1 inline-block text-sm font-medium text-slate-900 hover:text-black underline-offset-2 hover:underline transition"
                data-testid="store-link"
              >
                {storeName}
              </Link>
            ) : (
              <p className="mt-1 text-sm font-medium text-slate-900">—</p>
            )}
          </div>
          <DetailField label="Postazione" value={item.position || '—'} />
          <DetailField
            label="Affidato a"
            value={item.assignee_name || '—'}
          />
        </DetailCard>

        {/* STORICO MANUTENZIONI */}
        <DetailCard title="Storico manutenzioni" testId="card-maintenance">
          {maintenanceEvents.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Nessuna manutenzione registrata</p>
          ) : (
            <ul className="space-y-3">
              {maintenanceEvents.slice(0, 5).map(evt => {
                const Icon = TYPE_ICON[evt.type] || Wrench;
                return (
                  <li key={evt.event_id} className="flex items-start gap-3" data-testid={`maint-${evt.event_id}`}>
                    <Icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-500">
                        <span className="font-semibold uppercase">{TYPE_LABEL[evt.type]}</span>
                        {' · '}{new Date(evt.date).toLocaleDateString('it-IT')}
                        {evt.cost != null && <span> · €{Number(evt.cost).toFixed(2)}</span>}
                      </p>
                      <p className="text-sm text-slate-800 mt-0.5 truncate">{evt.description}</p>
                      {Array.isArray(evt.attachments) && evt.attachments.length > 0 && (
                        <p className="mt-1 text-[11px] text-slate-500 inline-flex items-center gap-1">
                          <Camera className="w-3 h-3" /> {evt.attachments.length} foto
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
              {maintenanceEvents.length > 5 && (
                <li className="text-xs text-slate-500 italic">
                  +{maintenanceEvents.length - 5} eventi precedenti
                </li>
              )}
            </ul>
          )}
        </DetailCard>
      </div>

      {/* ===== AZIONI MODIFICA / ELIMINA ===== */}
      <div className="flex items-center gap-2 pt-2">
        {canEdit && (
          <Button
            variant="outline"
            onClick={() => setShowEdit(true)}
            data-testid="edit-item-btn"
            className="rounded-md"
          >
            <Pencil className="w-4 h-4 mr-2" />Modifica
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => navigate(`/assistance?store_id=${item.store_id}`, {
            state: { openCreate: true, prefill: { store_id: item.store_id, item_id: item.item_id } },
          })}
          className="rounded-md"
          data-testid="open-ticket-btn"
        >
          <Wrench className="w-4 h-4 mr-2" />Apri ticket
        </Button>
        {canDelete && (
          <Button
            variant="outline"
            onClick={deleteItem}
            className="rounded-md text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            data-testid="delete-item-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />Elimina
          </Button>
        )}
      </div>

      {/* ===== FOTO DELL'OGGETTO (P1 idea_ae79a708845d) ===== */}
      <ItemAttachmentsCard
        itemId={itemId}
        attachments={attachments}
        uploading={uploadingAtt}
        currentUserId={user?.user_id}
        userRole={user?.role}
        isOps={isOps}
        onUpload={uploadItemFiles}
        onRemove={removeItemAttachment}
        onOpen={(a) => setItemLightbox({ attId: a.attachment_id, filename: a.filename })}
      />

      {/* ===== STORICO RI-ASSEGNAZIONI (P1) ===== */}
      {assignments.length > 0 && (
        <AssignmentHistoryCard rows={assignments} />
      )}

      {/* ===== NOTE / EVENTI AGGIUNTIVI ===== */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-5" data-testid="events-section">
        <div className="flex items-center justify-between">
          <h3 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">
            Note · manutenzioni · ricambi
          </h3>
          <span className="text-xs text-slate-500">{events.length} eventi</span>
        </div>

        {canAddNote && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-3" data-testid="add-event-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={eventForm.type} onValueChange={(v) => setEventForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Nota</SelectItem>
                    {canAddMaintenance && <SelectItem value="maintenance">Manutenzione</SelectItem>}
                    {canAddMaintenance && <SelectItem value="replacement">Ricambio</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Data</Label>
                <Input type="date" value={eventForm.date}
                       onChange={(e) => setEventForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              {canAddMaintenance && (eventForm.type === 'maintenance' || eventForm.type === 'replacement') && (
                <div>
                  <Label className="text-xs">Costo (€)</Label>
                  <Input type="number" step="0.01" value={eventForm.cost}
                         onChange={(e) => setEventForm(f => ({ ...f, cost: e.target.value }))} />
                </div>
              )}
            </div>
            <Textarea rows={2} value={eventForm.description}
                      onChange={(e) => setEventForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Descrizione dell'intervento o nota libera"
                      data-testid="event-desc" />
            <Button onClick={addEvent} disabled={savingEvt} data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-2" />{savingEvt ? 'Salvataggio…' : 'Aggiungi evento'}
            </Button>
          </div>
        )}

        {events.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Nessun evento registrato.</p>
        ) : (
          <ul className="space-y-3">
            {events.map(evt => {
              const Icon = TYPE_ICON[evt.type] || StickyNote;
              const canRemove = isOps || evt.created_by === user.user_id;
              return (
                <li key={evt.event_id} className="flex items-start gap-3 border-l-2 border-slate-300 pl-3"
                    data-testid={`event-${evt.event_id}`}>
                  <Icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500">
                      <span className="font-semibold uppercase">{TYPE_LABEL[evt.type]}</span>
                      {' · '}{new Date(evt.date).toLocaleDateString('it-IT')}
                      {' · '}{evt.created_by_email}
                      {evt.cost != null && <span> · €{Number(evt.cost).toFixed(2)}</span>}
                    </p>
                    <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{evt.description}</p>
                    {Array.isArray(evt.attachments) && evt.attachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid={`event-attachments-${evt.event_id}`}>
                        {evt.attachments.map(att => (
                          <button
                            key={att.attachment_id}
                            type="button"
                            onClick={() => setEventLightbox({
                              eventId: evt.event_id,
                              attId: att.attachment_id,
                              filename: att.filename,
                            })}
                            className="w-16 h-16 border border-slate-200 rounded overflow-hidden bg-slate-100 hover:border-slate-400 transition flex items-center justify-center"
                            title={att.filename || 'Foto'}
                            data-testid={`event-att-thumb-${att.attachment_id}`}
                          >
                            <img
                              src={inventoryApi.eventAttachmentUrl(evt.event_id, att.attachment_id)}
                              alt={att.filename || 'allegato'}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {canRemove && (
                    <button onClick={() => removeEvent(evt.event_id)}
                            className="text-slate-400 hover:text-red-600 transition"
                            data-testid={`remove-event-${evt.event_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {showEdit && (
        <EditItemDialog
          item={item}
          stores={stores}
          users={users}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); load(); }}
          isOps={isOps}
        />
      )}

      {eventLightbox && (
        <EventAttachmentLightbox
          eventId={eventLightbox.eventId}
          attId={eventLightbox.attId}
          filename={eventLightbox.filename}
          onClose={() => setEventLightbox(null)}
        />
      )}

      {itemLightbox && (
        <ItemAttachmentLightbox
          itemId={itemId}
          attId={itemLightbox.attId}
          filename={itemLightbox.filename}
          onClose={() => setItemLightbox(null)}
        />
      )}
    </div>
  );
}

function EventAttachmentLightbox({ eventId, attId, filename, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="event-attachment-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
        aria-label="Chiudi"
        data-testid="event-lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>
      <img
        src={inventoryApi.eventAttachmentUrl(eventId, attId)}
        alt={filename || 'allegato'}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-[90vh] object-contain shadow-2xl"
      />
    </div>
  );
}

function ItemAttachmentLightbox({ itemId, attId, filename, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="item-attachment-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
        aria-label="Chiudi"
        data-testid="item-lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>
      <img
        src={inventoryApi.attachmentUrl(itemId, attId)}
        alt={filename || 'allegato'}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-[90vh] object-contain shadow-2xl"
      />
    </div>
  );
}

function ItemAttachmentsCard({ itemId, attachments, uploading, currentUserId, userRole, isOps, onUpload, onRemove, onOpen }) {
  const fileRef = React.useRef(null);
  const remaining = Math.max(0, 10 - attachments.length);
  const isStoreUser = userRole === 'store_user';
  const PROMPT_KEY = 'inventory_photo_prompt_dismissed_v1';
  const [showPrompt, setShowPrompt] = React.useState(
    isStoreUser && attachments.length === 0 &&
    typeof window !== 'undefined' && !window.localStorage.getItem(PROMPT_KEY)
  );
  React.useEffect(() => {
    if (attachments.length > 0 && showPrompt) setShowPrompt(false);
  }, [attachments.length, showPrompt]);

  function dismissPrompt() {
    try { window.localStorage.setItem(PROMPT_KEY, '1'); } catch (e) { /* private mode */ }
    setShowPrompt(false);
  }
  return (
    <section
      className="bg-white border border-slate-200 rounded-lg p-6 space-y-4"
      data-testid="item-attachments-section"
    >
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold flex items-center gap-2">
          <Camera className="w-4 h-4" /> Foto dell'oggetto
          <span className="text-[11px] text-slate-400 tracking-normal normal-case">
            ({attachments.length}/10)
          </span>
        </h4>
        {remaining > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="rounded-md"
            data-testid="upload-item-attachment-btn"
          >
            <Upload className={`w-3.5 h-3.5 mr-1.5 ${uploading ? 'animate-pulse' : ''}`} />
            {uploading ? 'Caricamento…' : 'Aggiungi foto'}
          </Button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
        multiple
        className="hidden"
        onChange={(e) => { onUpload(e.target.files); e.target.value = ''; }}
        data-testid="item-attachment-file-input"
      />
      {showPrompt && (
        <div
          className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3"
          data-testid="store-user-photo-prompt"
        >
          <Camera className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900">
              Aggiungi una foto dell'attrezzo
            </p>
            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
              Una foto del prodotto, dell'etichetta o di un dettaglio aiuta a riconoscere e tracciare lo strumento in caso di guasti, sostituzioni o spostamenti.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button
                size="sm"
                onClick={() => { dismissPrompt(); fileRef.current?.click(); }}
                className="bg-amber-700 hover:bg-amber-800 rounded-md h-8 text-xs"
                data-testid="prompt-add-photo"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" />Aggiungi ora
              </Button>
              <button
                onClick={dismissPrompt}
                className="text-xs text-amber-800 hover:text-amber-900 underline underline-offset-2"
                data-testid="prompt-dismiss"
              >
                Più tardi
              </button>
            </div>
          </div>
        </div>
      )}
      {attachments.length === 0 ? (
        !showPrompt && (
          <p className="text-sm text-slate-500 italic">
            Nessuna foto caricata. Aggiungi foto dell'attrezzo, dell'etichetta o di un dettaglio identificativo.
          </p>
        )
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {attachments.map(att => {
            const canDel = isOps || att.uploaded_by === currentUserId;
            return (
              <div
                key={att.attachment_id}
                className="group relative aspect-square border border-slate-200 rounded overflow-hidden bg-slate-100"
                data-testid={`item-attachment-${att.attachment_id}`}
              >
                <button
                  type="button"
                  onClick={() => onOpen(att)}
                  className="absolute inset-0 w-full h-full"
                  title={att.filename || 'Foto'}
                >
                  <img
                    src={inventoryApi.attachmentUrl(itemId, att.attachment_id)}
                    alt={att.filename || 'allegato'}
                    className="w-full h-full object-cover hover:opacity-90 transition"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </button>
                {canDel && (
                  <button
                    type="button"
                    onClick={() => onRemove(att.attachment_id)}
                    className="absolute top-1 right-1 bg-white/90 hover:bg-white text-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition shadow-sm"
                    aria-label="Rimuovi"
                    data-testid={`remove-item-att-${att.attachment_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ============================================================
// Sub-components
// ============================================================
function DetailCard({ title, children, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid={testId}>
      <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-4">{title}</h4>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function DetailField({ label, value, multiline }) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`mt-1 text-sm font-medium text-slate-900 ${multiline ? 'whitespace-pre-wrap' : ''}`}>
        {value}
      </p>
    </div>
  );
}

function EditItemDialog({ item, stores, users, onClose, onSaved, isOps }) {
  const [form, setForm] = useState({
    code: item.code || '',
    model: item.model || '',
    description: item.description || '',
    purchase_date: item.purchase_date || '',
    purchase_price: item.purchase_price ?? '',
    store_id: item.store_id || '',
    position: item.position || '',
    assigned_user_id: item.assigned_user_id || '',
  });
  const [saving, setSaving] = useState(false);
  // Lista combinata staff+manager dal backend (refactor 2026-05-29).
  // In produzione i ~110 staff_members non sono linkati ad account `users`,
  // quindi il vecchio filtro `users.role==store_user` lasciava il dropdown
  // vuoto. Ora i barbieri/dipendenti sono cittadini di prima classe; gli
  // account dell'app servono solo per i manager (cross-store).
  const { assignees, loading: assigneesLoading } = useAssignees();
  const groupedAssignees = groupAssignees(assignees, form.store_id);

  async function submit() {
    setSaving(true);
    try {
      const payload = { ...form };
      // Solo OPS può modificare oltre `assigned_user_id`; gli AM ricevono 403 per altri campi
      if (!isOps) {
        Object.keys(payload).forEach(k => { if (k !== 'assigned_user_id') delete payload[k]; });
      }
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (payload.purchase_price != null && payload.purchase_price !== null) {
        payload.purchase_price = Number(payload.purchase_price) || null;
      }
      await inventoryApi.updateItem(item.item_id, payload);
      toast.success('Oggetto aggiornato');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Modifica oggetto · {item.code}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {isOps && (
            <>
              <section>
                <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3 pb-2 border-b border-slate-200">Identificazione</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-xs">Codice</Label>
                    <Input value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value }))}
                           data-testid="edit-code" />
                    <p className="text-[10px] text-slate-400 mt-1">Deve essere univoco. Attenzione: cambiandolo, le etichette QR già stampate restano valide (puntano alla scheda, non al codice).</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Modello / marca</Label>
                    <Input value={form.model} onChange={(e) => setForm(f => ({ ...f, model: e.target.value }))}
                           data-testid="edit-model" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Note aggiuntive</Label>
                    <Textarea rows={2} value={form.description}
                              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                              data-testid="edit-desc" />
                  </div>
                </div>
              </section>

              <section>
                <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3 pb-2 border-b border-slate-200">Acquisto</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Data di acquisto</Label>
                    <Input type="date" value={form.purchase_date}
                           onChange={(e) => setForm(f => ({ ...f, purchase_date: e.target.value }))}
                           data-testid="edit-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Prezzo di acquisto (€)</Label>
                    <Input type="number" step="0.01" value={form.purchase_price}
                           onChange={(e) => setForm(f => ({ ...f, purchase_price: e.target.value }))}
                           data-testid="edit-price" />
                  </div>
                </div>
              </section>
            </>
          )}

          <section>
            <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3 pb-2 border-b border-slate-200">Ubicazione</h4>
            <div className="grid grid-cols-2 gap-4">
              {isOps && (
                <div>
                  <Label className="text-xs">Negozio</Label>
                  <Select value={form.store_id || ''}
                          onValueChange={(v) => setForm(f => ({ ...f, store_id: v, assigned_user_id: '' }))}>
                    <SelectTrigger data-testid="edit-store"><SelectValue placeholder="— seleziona —" /></SelectTrigger>
                    <SelectContent>
                      {[...stores]
                        .sort((a, b) => {
                          const aSp = a.kind === 'warehouse' || a.kind === 'academy';
                          const bSp = b.kind === 'warehouse' || b.kind === 'academy';
                          if (aSp !== bSp) return aSp ? 1 : -1;
                          return (a.display_name || '').localeCompare(b.display_name || '');
                        })
                        .map(s => (
                          <SelectItem key={s.store_id} value={s.store_id}>
                            {s.display_name || s.name}
                            {(s.kind === 'warehouse' || s.kind === 'academy') && (
                              <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">Sede</span>
                            )}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isOps && (
                <div>
                  <Label className="text-xs">Postazione</Label>
                  <Input value={form.position}
                         onChange={(e) => setForm(f => ({ ...f, position: e.target.value }))}
                         placeholder="es. Postazione 1, Lavabo B..."
                         data-testid="edit-position" />
                </div>
              )}
              <div className={isOps ? 'col-span-2' : 'col-span-2'}>
                <Label className="text-xs">Affidato a</Label>
                <Select value={form.assigned_user_id || 'none'}
                        onValueChange={(v) => setForm(f => ({ ...f, assigned_user_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="edit-assignee">
                    <SelectValue placeholder={assigneesLoading ? 'Caricamento…' : '— nessuno —'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[60vh]">
                    <SelectItem value="none">— nessuno —</SelectItem>
                    {groupedAssignees.sameStoreStaff.length > 0 && (
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50 sticky top-0">
                        Staff del negozio
                      </div>
                    )}
                    {groupedAssignees.sameStoreStaff.map(a => (
                      <SelectItem key={a.id} value={a.id} data-testid={`assignee-${a.id}`}>
                        <span className="font-medium">{a.name}</span>
                        {a.role && <span className="ml-2 text-[10px] text-slate-500 uppercase tracking-wider">{a.role}</span>}
                      </SelectItem>
                    ))}
                    {groupedAssignees.otherStaff.length > 0 && (
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50">
                        Altri staff
                      </div>
                    )}
                    {groupedAssignees.otherStaff.map(a => (
                      <SelectItem key={a.id} value={a.id} data-testid={`assignee-${a.id}`}>
                        <span className="font-medium">{a.name}</span>
                        {a.store_name && <span className="ml-2 text-[10px] text-slate-500">· {a.store_name}</span>}
                      </SelectItem>
                    ))}
                    {groupedAssignees.managers.length > 0 && (
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50">
                        Manager
                      </div>
                    )}
                    {groupedAssignees.managers.map(a => (
                      <SelectItem key={a.id} value={a.id} data-testid={`assignee-${a.id}`}>
                        <span className="font-medium">{a.name}</span>
                        <span className="ml-2 text-[10px] text-slate-500 uppercase tracking-wider">{a.role}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="edit-save-btn">
            {saving ? 'Salvataggio…' : 'Salva modifiche'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const REASON_LABEL = {
  update: 'Aggiornamento manuale',
  bulk_reassign: 'Spostamento in blocco',
  create: 'Creazione iniziale',
};

function AssignmentHistoryCard({ rows }) {
  return (
    <section
      className="bg-white border border-slate-200 rounded-lg p-6 space-y-4"
      data-testid="assignment-history-section"
    >
      <h4 className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold flex items-center gap-2">
        <History className="w-4 h-4" /> Storico assegnazioni
        <span className="text-[11px] text-slate-400 tracking-normal normal-case">
          ({rows.length})
        </span>
      </h4>
      <ol className="space-y-3">
        {rows.map(r => {
          const dt = r.changed_at ? new Date(r.changed_at) : null;
          const dateStr = dt ? dt.toLocaleString('it-IT', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
          }) : '—';
          const storeChanged = r.from_store_id !== r.to_store_id;
          const userChanged = r.from_user_id !== r.to_user_id;
          return (
            <li
              key={r.assignment_id}
              className="flex gap-3 border-l-2 border-slate-200 pl-3"
              data-testid={`assignment-row-${r.assignment_id}`}
            >
              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-[11px] text-slate-500">
                  <span className="font-mono">{dateStr}</span>
                  {' · '}
                  <span className="text-slate-700">{REASON_LABEL[r.reason] || r.reason}</span>
                  {' · '}
                  <span className="text-slate-600">{r.changed_by_email || '—'}</span>
                </p>
                {storeChanged && (
                  <p className="text-sm text-slate-800">
                    <span className="text-slate-500">Negozio:</span>{' '}
                    <span>{r.from_store_name || '—'}</span>
                    <span className="text-slate-400 mx-1.5">→</span>
                    <span className="font-medium">{r.to_store_name || '—'}</span>
                  </p>
                )}
                {userChanged && (
                  <p className="text-sm text-slate-800">
                    <span className="text-slate-500">Affidato a:</span>{' '}
                    <span>{r.from_user_name || 'nessuno'}</span>
                    <span className="text-slate-400 mx-1.5">→</span>
                    <span className="font-medium">{r.to_user_name || 'nessuno'}</span>
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
